import BaseAccordion from 'components/Accordions/BaseAccordion';
import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { RiAlertFill } from 'react-icons/ri';
import Popup from 'reactjs-popup';
import { useGetDevicesByIdsQuery } from 'services/devices/devices.api';
import { useGetGlobalActionsQuery } from 'services/globalActions/globalActions.api';
import { capitalizeWord } from 'utils/strings/string.utils';
import {
  getRelatedDevicesFromGlobalAction,
  isMissingDevice,
  shouldHideGlobalActionFromList,
} from '../utils/actionListUtils';
import { formatGlobalActionRecentActivity } from '../utils/activityUtils';

const Accordions = ({ onView, onEdit, onRemove, enableGlobalAction, searchFilter }) => {
  const { data: globalActions = [] } = useGetGlobalActionsQuery();

  // Extract all device IDs referenced by the current global actions
  const deviceIds = useMemo(() => {
    const ids = new Set();
    globalActions.forEach((action) => {
      getRelatedDevicesFromGlobalAction(action).forEach((id) => ids.add(id));
    });
    const arr = Array.from(ids);
    return arr;
  }, [globalActions]);

  // Only fetch devices that are referenced in the table
  const { data: deviceList = [], isLoading: isDeviceListLoading } = useGetDevicesByIdsQuery(
    deviceIds,
    {
      skip: deviceIds.length === 0,
    },
  );

  const perms = usePerms();
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});
  const filteredGlobalActions = globalActions
    .filter((item) => !shouldHideGlobalActionFromList(item))
    .filter(searchFilter);

  return (
    <div className="d-block d-lg-none mt-3">
      {filteredGlobalActions.map((item, index) => (
        <div className={index !== filteredGlobalActions.length - 1 ? 'mb-3' : ''} key={item.id}>
          <BaseAccordion
            key={item.id}
            bodyBackground="#232B33"
            header={
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-column">
                  {item.title}
                  <div>
                    {capitalizeWord(item.state)} {/* Warning Triangle if missing device */}
                    {!isDeviceListLoading && isMissingDevice(item, deviceList) && (
                      <div className="d-inline-block">
                        <Popup
                          on="hover"
                          position={'top center'}
                          trigger={
                            <div>
                              <RiAlertFill color="yellow" size={20} />
                            </div>
                          }
                        >
                          <div className="text-white p-2 rounded-2 bg-primary-shadow">
                            Missing Device
                          </div>
                        </Popup>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`border border-1 border-white rounded-3 d-flex align-items-center justify-content-center ${
                    item.status ? 'bg-success' : 'bg-primary-shadow'
                  }`}
                  style={{ width: 88, height: 27 }}
                >
                  {item.status ? 'Online' : 'Offline'}
                </div>
              </div>
            }
          >
            <div className="d-flex justify-content-between">
              <p>Trigger: {item.triggerName}</p>
              <BaseEllipsisMenu
                options={[
                  {
                    component: (
                      <>
                        {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewGlobalActions']) && (
                          <Button
                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                            onClick={() => onView(item)}
                            variant="secondary"
                          >
                            View
                          </Button>
                        )}
                      </>
                    ),
                  },
                  {
                    component: (
                      <>
                        {perms.testPermission(
                          [8, 9, 10, 11, 12, 13],
                          ['Create-EditGlobalActions'],
                        ) && (
                          <Button
                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                            onClick={() => onEdit(item)}
                            variant="secondary"
                          >
                            Edit
                          </Button>
                        )}
                      </>
                    ),
                  },
                  {
                    component: (
                      <>
                        {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteGlobalActions']) && (
                          <Button
                            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                            onClick={() => onRemove(item)}
                            variant="danger"
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    ),
                  },
                ]}
                open={rowIdEllipsisOpen[item.id]}
                onOpen={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [item.id]: true,
                  })
                }
                onClose={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [item.id]: false,
                  })
                }
              />
            </div>
            <p>Action: {item.actionName}</p>
            <p>Resolution: {item.resolutionName}</p>
            <p>Recent Activity: {formatGlobalActionRecentActivity(item)}</p>
            <div className="d-flex justify-content-center mt-2">
              <div style={{ width: '200px' }}>
                {perms.testPermission([8, 9, 10, 11, 12, 13], ['Enable-DisableGlobalActions']) ? (
                  <TextToggle
                    options={['Offline', 'Online']}
                    value={item.status}
                    setValue={(value) => enableGlobalAction(item.id, value)}
                  />
                ) : (
                  <div
                    className="p-1 text-center rounded-3 text-white"
                    style={{
                      backgroundColor: item.status ? '#01B574' : '',
                      border: item.status ? '0px' : '1px solid #ccc',
                      width: 80,
                    }}
                  >
                    {item.status ? 'Online' : 'Offline'}
                  </div>
                )}
              </div>
            </div>
          </BaseAccordion>
        </div>
      ))}
    </div>
  );
};

export default Accordions;
